// Auth wraps the old backend's checkLoginVerify (no per-user tokens) and
// synthesizes a client-side session, per the agreed migration approach.
// See docs/API_MIGRATION_MAPPING.md ("Auth model gap").
import {
  addUpdateTemplateDataWeb,
  cell,
  checkLoginVerify,
  describeExpiredCredentialError,
  getCrmTemplateDataList,
  getMaxRecordidInTemplate,
  getTemplateDataList,
  isCrmApiConnected,
  isExpiredCredentialError,
} from "@/lib/apiClient";
import { cacheStudentProfile, clearCachedStudentProfile, transformRow as transformStudentRow } from "@/services/studentService";

const SESSION_KEY = "nimas_session";
const SESSION_TTL_MS = 12 * 60 * 60 * 1000; // 12h, client-side only (old backend has no expiry concept)
const REMEMBER_ME_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days, when "Remember me" is checked
const ADMIN_EMAILS = ["nimasdirangindia@gmail.com"];

const STUDENT_SCHEMA = {
  id: 0,
  fullName: 5,
  mobile: 6,
  email: 8,
  gender: 10,
  dob: 11,
} as const;

export interface Session {
  id: string;
  email: string;
  fullName: string;
  role: "student" | "admin";
  exp: number;
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (session.exp < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function setSession(session: Session | null) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
}

interface ResolveEmailResult {
  email: string | null;
  /** Set when the CRM lookup itself failed (e.g. expired credential) — distinct from a genuine "no such username" so login() doesn't misreport an outage as a typo. */
  lookupError?: string;
}

async function resolveEmail(identifier: string): Promise<ResolveEmailResult> {
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
  if (isEmail) return { email: identifier };

  // Students(4) rejects ALL reads through getTemplateDataList regardless of
  // filters, so username lookup has to go through the CRM route instead.
  if (!isCrmApiConnected()) {
    return { email: null, lookupError: "CRM API is not configured (missing VITE_API_CRM_AUTH_TOKEN)." };
  }
  const envelope = await getCrmTemplateDataList(4, { filters: { "Full Name": identifier }, pgSize: 5 });
  if (envelope.hasErrors) {
    const lookupError = isExpiredCredentialError(envelope) ? describeExpiredCredentialError(envelope) : envelope.errors || "The CRM API reported an error.";
    return { email: null, lookupError };
  }
  const row = envelope.response?.records?.[0];
  return { email: row ? cell(row, STUDENT_SCHEMA.email) : null };
}

function ttlFor(rememberMe: boolean): number {
  return Date.now() + (rememberMe ? REMEMBER_ME_TTL_MS : SESSION_TTL_MS);
}

/**
 * Student-only login — the dedicated /student/login page. Accepts email or
 * username (resolved via the CRM route, same as before). Deliberately does
 * NOT fall back to an admin check on failure the way the old combined
 * login() did — if the credential turns out to belong to an admin account,
 * says so explicitly instead of silently logging them into the wrong portal.
 */
export async function loginStudent(
  identifier: string,
  password: string,
  rememberMe = false
): Promise<{ error: string | null; role: string | null }> {
  const { email, lookupError } = await resolveEmail(identifier);
  if (lookupError) {
    // The username lookup itself failed (e.g. expired CRM credential) — say
    // so plainly rather than telling the user their username is wrong.
    return { error: `Unable to look up your account right now (${lookupError}). Please try your email address instead, or contact support.`, role: null };
  }
  if (!email) {
    return { error: "No account found with that username. Please try your email address.", role: null };
  }

  const studentResult = await checkLoginVerify(email, password);
  if (!studentResult.hasErrors && studentResult.response?.length > 0) {
    const row = studentResult.response[0];
    const session: Session = {
      id: cell(row, STUDENT_SCHEMA.id),
      email: cell(row, STUDENT_SCHEMA.email),
      fullName: cell(row, STUDENT_SCHEMA.fullName),
      role: "student",
      exp: ttlFor(rememberMe),
    };
    setSession(session);
    // checkLoginVerify is the only live read path that actually returns
    // student data (getTemplateDataList rejects all reads of this
    // template) — cache the full row here so profile prefill / self-view
    // has something to fall back on. See studentService.getStudentProfileByEmail.
    cacheStudentProfile(transformStudentRow(row));
    return { error: null, role: "student" };
  }

  // Wrong password for a genuine student account looks identical to "this
  // is actually an admin account" from this endpoint's response alone — the
  // only way to tell them apart is to also try the admin check. Only do
  // this to produce a better error message, never to log the user in via
  // the wrong portal.
  const adminCheck = await checkLoginVerify(email, password, 2);
  if (!adminCheck.hasErrors && adminCheck.response?.length > 0) {
    return { error: "This is an admin account. Please use the Admin Login page instead.", role: null };
  }

  return { error: studentResult.errors || "Invalid username or password", role: null };
}

/**
 * Admin-only login — the dedicated /admin/login page. Email only (there is
 * no CRM-equivalent username lookup for Admins(7); templateID 7 rejects all
 * reads through the normal endpoint, and no CRM read path was ever confirmed
 * for it — see docs/BACKEND_SPEC_MISSING_ENDPOINTS.md). Deliberately does
 * NOT fall back to a student check on failure — if the credential belongs
 * to a student, says so explicitly instead of silently logging them in.
 */
export async function loginAdmin(
  email: string,
  password: string,
  rememberMe = false
): Promise<{ error: string | null; role: string | null }> {
  const adminResult = await checkLoginVerify(email, password, 2);
  if (!adminResult.hasErrors && adminResult.response?.length > 0) {
    const row = adminResult.response[0];
    const session: Session = {
      id: cell(row, 0),
      email,
      fullName: cell(row, 5) || email.split("@")[0],
      role: "admin",
      exp: ttlFor(rememberMe),
    };
    setSession(session);
    return { error: null, role: "admin" };
  }

  // Same credential possibly belongs to a student — check to give a
  // specific, correct error instead of a generic "invalid password".
  const studentCheck = await checkLoginVerify(email, password);
  if (!studentCheck.hasErrors && studentCheck.response?.length > 0) {
    return { error: "This is a student account. Please use the Student Login page instead.", role: null };
  }

  return { error: adminResult.errors || "Invalid email or password", role: null };
}

export interface SignupInput {
  email: string;
  password: string;
  fullName: string;
  gender: string;
  dob: string;
  mobile: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  /** Uploaded profile picture URL (via apiClient.uploadFile), optional — old Register.js's own upload is optional too. */
  avatarUrl?: string;
}

export async function signup(input: SignupInput): Promise<{ error: string | null; alreadyExists?: boolean }> {
  if (ADMIN_EMAILS.includes(input.email.toLowerCase())) {
    return { error: "This email is reserved for an administrator account. Please use a different email for student registration." };
  }

  // No duplicate-email pre-check: the Students template (4) rejects ALL
  // reads through getTemplateDataList — confirmed live, not filter-specific
  // — so this can never actually detect an existing account. The old app
  // never checked either; a duplicate signup here just creates a second row
  // with the same email (checkLoginVerify — the only working read path for
  // this template — would then return the first match on login).

  const { response: maxIdResp } = await getMaxRecordidInTemplate(4);
  const nextId = maxIdResp?.maxid ?? 1;

  // Age, same calculation as old Register.js's fun_signUP (Math.floor of
  // years between DOB and now) — stored at _20 (read index 22), matching
  // studentService.ts's SCHEMA.age.
  const age = String(Math.floor((Date.now() - new Date(input.dob).getTime()) / 1000 / 60 / 60 / 24 / 365.25));

  // Students (templateID 4) has exactly 28 registered fields (_1.._28) — see
  // studentService.ts's SCHEMA comment for the confirmed-live field mapping.
  // Matches old Register.js's signUPpara field-for-field; _21.._28
  // (NextOfKin/HeightinCM/WeightinKG/BMI/ShoeSize/AcademicQualification/
  // Hobbies/NextOfKinAddress&Telephone) aren't collected at signup, but per
  // this backend's CREATE-must-include-every-field-up-to-the-highest-index-
  // in-use quirk they still have to be sent as blanks — omitting them
  // entirely permanently drops those slots from the row, corrupting every
  // later profile-update write's field alignment.
  await addUpdateTemplateDataWeb(4, {
    _1: nextId,
    _2: input.avatarUrl || "",
    _3: input.fullName,
    _4: input.mobile,
    _5: "",
    _6: input.email,
    _7: input.password,
    _8: input.gender,
    _9: input.dob,
    _10: input.address,
    _11: input.city,
    _12: input.state,
    _13: input.pincode,
    _14: input.country,
    _15: "",
    _16: "",
    _17: "",
    _18: "",
    _19: "isStudent",
    _20: age,
    _21: "",
    _22: "",
    _23: "",
    _24: "",
    _25: "",
    _26: "",
    _27: "",
    _28: "",
  });

  return { error: null };
}

export async function logout(): Promise<void> {
  // Old backend has no logout endpoint (session was never server-tracked); clear locally only.
  setSession(null);
  clearCachedStudentProfile();
}

export async function refreshSession(): Promise<boolean> {
  const session = getSession();
  if (!session) return false;
  setSession({ ...session, exp: Date.now() + SESSION_TTL_MS });
  return true;
}

export async function setupAdminAccount(email: string, password: string): Promise<{ success: boolean; error?: string }> {
  const existing = await getTemplateDataList(7, { Email: email }, { pgSize: 1 });
  const row = existing.response.records?.[0];

  if (row) {
    await addUpdateTemplateDataWeb(7, { _6: email, _7: password, _19: "isAdmin" }, cell(row, 0));
  } else {
    const { response: maxIdResp } = await getMaxRecordidInTemplate(7);
    const nextId = maxIdResp?.maxid ?? 1;
    await addUpdateTemplateDataWeb(7, {
      _1: nextId,
      _3: "Admin",
      _6: email,
      _7: password,
      _19: "isAdmin",
    });
  }

  return { success: true };
}
